import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import {
  fetchRoles,
  fetchPermissions,
  createRole,
  updateRole,
  deleteRole,
  setSelectedRole,
} from "../../store/slices/rbacSlice";
import { Role, Permission, RoleFormData } from "../../types/rbac";
import { PermissionGuard } from "../../components/common/PermissionGuard";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import BaseModal from "../../components/common/BaseModal";
import { masterDropdownService } from "../../services/masterDropdownService";
import { rbacService } from "../../services/rbacService";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { AVAILABLE_REPORTS } from "../../config/reports";

const RoleManager: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    roles,
    permissions,
    permissionsByModule,
    isLoading,
    error,
    selectedRole,
  } = useSelector((state: RootState) => state.rbac);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState<RoleFormData>({
    name: "",
    description: "",
    permissions: {},
  });
  const [roleTypeOptions, setRoleTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingRoleTypes, setLoadingRoleTypes] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string>('');

  const loadRoleTypes = async () => {
    try {
      setLoadingRoleTypes(true);
      const options = await masterDropdownService.getRoleTypesDropdown();
      // Transform role names to display labels (capitalize and replace underscores)
      const transformedOptions = options.map(option => ({
        value: option.value,
        label: option.label
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      }));
      setRoleTypeOptions(transformedOptions);
    } catch (error) {
      console.error('Failed to load role types from master:', error);
      setRoleTypeOptions([]);
    } finally {
      setLoadingRoleTypes(false);
    }
  };

  const syncRoleTypesToRoles = async (showMessage: boolean = true) => {
    try {
      setSyncing(true);
      if (showMessage) {
        setSyncMessage('');
      }
      
      // Sync role types from master to roles table
      const syncResult = await rbacService.syncRoleTypesFromMaster();
      console.log('Sync result:', syncResult);
      
      // Refresh role types dropdown
      await loadRoleTypes();
      
      // Refresh roles list
      await dispatch(fetchRoles());
      
      // Show success message only if requested
      if (showMessage) {
        if (syncResult.created.length > 0) {
          setSyncMessage(`Successfully synced ${syncResult.created.length} new role(s): ${syncResult.created.map(r => r.name).join(', ')}`);
        } else {
          setSyncMessage('All role types are already synced. No new roles created.');
        }
        
        // Clear message after 5 seconds
        setTimeout(() => setSyncMessage(''), 5000);
      }
    } catch (error: any) {
      console.error('Failed to sync role types:', error);
      if (showMessage) {
        setSyncMessage(error.response?.data?.error?.message || error.response?.data?.message || 'Failed to sync role types');
        setTimeout(() => setSyncMessage(''), 5000);
      }
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      // First sync role types from master to ensure all role types have corresponding roles (silent)
      await syncRoleTypesToRoles(false);
      // Then load all data
      dispatch(fetchRoles());
      dispatch(fetchPermissions());
      loadRoleTypes();
    };
    initializeData();
  }, [dispatch]);

  const handleCreateRole = async () => {
    if (!formData.name) {
      alert("Please select a role type from the dropdown");
      return;
    }
    try {
      await dispatch(createRole(formData));
      setShowCreateModal(false);
      setFormData({ name: "", description: "", permissions: {} });
      // Refresh role types in case new ones were added
      await loadRoleTypes();
    } catch (error) {
      console.error("Failed to create role:", error);
    }
  };

  const handleEditRole = async () => {
    if (!selectedRole) return;

    if (!formData.name) {
      alert("Please select a role type from the dropdown");
      return;
    }

    try {
      await dispatch(updateRole({ id: selectedRole.id, roleData: formData }));
      setShowEditModal(false);
      setFormData({ name: "", description: "", permissions: {} });
      // Refresh role types in case new ones were added
      await loadRoleTypes();
    } catch (error) {
      console.error("Failed to update role:", error);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (window.confirm("Are you sure you want to delete this role?")) {
      try {
        await dispatch(deleteRole(roleId));
      } catch (error) {
        console.error("Failed to delete role:", error);
      }
    }
  };

  const handleEditClick = (role: Role) => {
    dispatch(setSelectedRole(role));
    setFormData({
      name: role.name,
      description: role.description || "",
      permissions: role.permissions,
    });
    setShowEditModal(true);
    // Refresh role types when opening edit modal
    loadRoleTypes();
  };

  const handlePermissionChange = (
    module: string,
    action: string,
    checked: boolean
  ) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        permissions: {
          ...prev.permissions,
          [module]: {
            ...prev.permissions[module],
            [action]: checked,
          },
        },
      };

      // If turning off a parent module's read, clear all child actions
      if (action === "read" && checked === false) {
        const parent = module;
        const children = permissions
          .filter((p) => p.parent_module === parent)
          .map((p) => p.module)
          .filter((v, i, arr) => arr.indexOf(v) === i);
        if (children.length) {
          const updated = { ...next.permissions } as Record<string, Record<string, boolean>>;
          children.forEach((child) => {
            const current = updated[child] || {};
            const cleared: Record<string, boolean> = {};
            Object.keys(current).forEach((a) => {
              cleared[a] = false;
            });
            updated[child] = cleared;
          });
          return { ...next, permissions: updated } as any;
        }
      }

      return next;
    });
  };

  const renderPermissionCheckboxes = () => {
    // Build parent-child maps from permissions list
    const moduleToParent: Record<string, string> = {};
    const parentToChildren: Record<string, string[]> = {};
    permissions.forEach((p) => {
      if (p.parent_module) {
        moduleToParent[p.module] = p.parent_module;
        if (!parentToChildren[p.parent_module]) parentToChildren[p.parent_module] = [];
        if (!parentToChildren[p.parent_module].includes(p.module)) {
          parentToChildren[p.parent_module].push(p.module);
        }
      }
    });

    // Determine unique modules
    const uniqueModules = Array.from(
      new Set(permissions.map((p) => p.module))
    );

    // Order: parents first, then children under each parent
    const parents = uniqueModules.filter((m) => !moduleToParent[m]);
    const ordered: string[] = [];
    parents.forEach((parent) => {
      ordered.push(parent);
      (parentToChildren[parent] || []).forEach((c) => {
        if (!ordered.includes(c)) ordered.push(c);
      });
    });
    uniqueModules.forEach((m) => {
      if (!ordered.includes(m)) ordered.push(m);
    });

    const handleReportPermissionChange = (reportId: string, checked: boolean) => {
      setFormData((prev) => {
        const reportsModule = prev.permissions['reports'] || {};
        return {
          ...prev,
          permissions: {
            ...prev.permissions,
            reports: {
              ...reportsModule,
              [reportId]: {
                ...(reportsModule[reportId] || {}),
                read: checked,
              },
            },
          },
        };
      });
    };

    return ordered.map((module) => {
      const modulePermissions = permissions.filter((p) => p.module === module);
      const parent = moduleToParent[module];
      const isChild = !!parent;
      const parentReadEnabled = !isChild || (formData.permissions[parent]?.read === true);
      const isReportsModule = module === 'reports';

      return (
        <React.Fragment key={module}>
          <div className={`mb-6 ${isChild ? "ml-4 border-l pl-4" : ""}`}>
            <h3 className="text-lg font-semibold mb-1 capitalize flex items-center gap-2">
              {module}
              {isChild && (
                <span className="text-xs font-normal text-gray-500">(Child of {parent})</span>
              )}
            </h3>
            {!isChild && (parentToChildren[module]?.length || 0) > 0 && (
              <p className="text-xs text-gray-500 mb-2">
                Enabling Read will unlock configuration for its child modules.
              </p>
            )}
            {!parentReadEnabled && isChild ? (
              <p className="text-sm text-gray-500">
                Enable {parent} Read to configure {module}.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {modulePermissions.map((permission: Permission) => (
                  <label key={permission.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.permissions[module]?.[permission.action] || false}
                      onChange={(e) =>
                        handlePermissionChange(module, permission.action, e.target.checked)
                      }
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm capitalize">{permission.action}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          
          {/* Show individual reports when reports module is visible */}
          {isReportsModule && (
            <div className="ml-4 border-l pl-4 mb-6">
              <h4 className="text-sm font-semibold mb-2 text-gray-700">Individual Reports:</h4>
              <div className="space-y-2">
                {AVAILABLE_REPORTS.map((report) => {
                  const reportPermission = formData.permissions['reports']?.[report.id];
                  const isReportChecked = reportPermission?.read === true;

                  return (
                    <label key={report.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={isReportChecked}
                        onChange={(e) => handleReportPermissionChange(report.id, e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-600">{report.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </React.Fragment>
      );
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Role Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage role permissions. Role types are managed in Masters → Role Types Management.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => syncRoleTypesToRoles(true)}
            disabled={syncing || loadingRoleTypes}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            title="Sync role types from Masters to create missing roles"
          >
            <ArrowPathIcon className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from Masters'}
          </button>
          <PermissionGuard module="users" action="manage_roles">
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Create New Role
            </button>
          </PermissionGuard>
        </div>
      </div>

      {syncMessage && (
        <div className={`px-4 py-3 rounded mb-4 ${
          syncMessage.includes('Successfully') || syncMessage.includes('already synced')
            ? 'bg-green-100 border border-green-400 text-green-700'
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          {syncMessage}
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="grid gap-4">
        {roles.map((role) => (
          <div key={role.id} className="bg-white p-4 rounded-lg shadow border">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold">{role.name}</h3>
                <p className="text-gray-600">{role.description}</p>
                <div className="mt-2">
                  <span
                    className={`inline-block px-2 py-1 text-xs rounded ${
                      role.is_system_role
                        ? "bg-blue-100 text-blue-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {role.is_system_role ? "System Role" : "Custom Role"}
                  </span>
                </div>
              </div>
              <div className="flex space-x-2">
                <PermissionGuard module="users" action="manage_roles">
                  <button
                    onClick={() => handleEditClick(role)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Edit
                  </button>
                </PermissionGuard>
                {!role.is_system_role && (
                  <PermissionGuard module="users" action="manage_roles">
                    <button
                      onClick={() => handleDeleteRole(role.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </PermissionGuard>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create Role Modal */}
      <BaseModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Role"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Role Type <span className="text-red-500">*</span>
            </label>
            {loadingRoleTypes ? (
              <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50">
                Loading role types...
              </div>
            ) : (
              <select
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              >
                <option value="">Select a role type</option>
                {roleTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Role types are managed in Masters → Role Types Management. Add new role types there first.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              rows={3}
              placeholder="Enter role description"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Permissions
            </label>
            <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-lg p-4">
              {renderPermissionCheckboxes()}
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowCreateModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateRole}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Role
            </button>
          </div>
        </div>
      </BaseModal>

      {/* Edit Role Modal */}
      <BaseModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Role"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Role Type <span className="text-red-500">*</span>
            </label>
            {loadingRoleTypes ? (
              <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50">
                Loading role types...
              </div>
            ) : (
              <select
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 ${
                  selectedRole?.is_system_role ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                disabled={selectedRole?.is_system_role}
                required
              >
                <option value="">Select a role type</option>
                {roleTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            {selectedRole?.is_system_role && (
              <p className="mt-1 text-xs text-amber-600">
                System role name cannot be changed. Only permissions can be updated.
              </p>
            )}
            {!selectedRole?.is_system_role && (
              <p className="mt-1 text-xs text-gray-500">
                Role types are managed in Masters → Role Types Management. Add new role types there first.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              rows={3}
              placeholder="Enter role description"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Permissions
            </label>
            <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-lg p-4">
              {renderPermissionCheckboxes()}
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowEditModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleEditRole}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Update Role
            </button>
          </div>
        </div>
      </BaseModal>
    </div>
  );
};

export default RoleManager;
